import React from "react";
import clsx from "clsx";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@emotion/react";
import { useStyles } from "./style";
import PropsTypes from "prop-types";
import { Box, Tooltip } from "@mui/material";
import LoadingSpinner from "components/loadingSpinner";
import constants from "../../constants/constants.json";
import { tranformResultsForTaka41, getSimplifiedAdvId } from "./utils";

function getCellStyle(order, isLastRow, isDarkTheme, variant = "taka") {
  const borderRight = isDarkTheme ? { borderRight: "2px solid #A8ABC2" } : {};
  const solidBorder = { borderRight: "2px solid #A8ABC2" };

  // Common minWidth values
  const minWidth100 = { minWidth: 100 };
  const minWidth110 = { minWidth: 110 };
  const minWidth130 = { minWidth: 130 };
  const minWidth140 = { minWidth: 140 };

  const commonStyle = {
    0: {},
    1: {},
    2: borderRight,
    3: {},
    4: { ...minWidth110, ...borderRight },
  };

  // Common styles for taka41a and taka41c variants
  const taka41CommonStyle = {
    ...commonStyle,
    5: minWidth100,
    6: minWidth110,
    7: { ...minWidth110, ...borderRight },
    8: minWidth100,
    9: minWidth110,
    13: { maxWidth: 50, ...solidBorder },
  };

  const styleMap = {
    taka: {
      ...commonStyle,
      5: minWidth100,
      6: { ...minWidth110, ...borderRight },
      7: minWidth100,
      8: { ...minWidth140, ...borderRight },
      9: {},
      10: {},
      11: {},
      12: { maxWidth: 50, ...solidBorder },
    },
    taka41a: {
      ...taka41CommonStyle,
      10: minWidth110,
      11: { ...minWidth130, ...borderRight },
      12: minWidth100,
    },
    taka41c: {
      ...taka41CommonStyle,
      10: minWidth130,
      11: { ...minWidth100, ...borderRight },
      12: {},
    },
  };

  return {
    ...(isLastRow ? { borderBottom: "none" } : {}),
    ...(styleMap[variant][order] || {}),
  };
}

function isAssembly(order, variant) {
  if (variant === "taka41a" || variant === "taka41c") {
    return [5, 6, 7].includes(order);
  }

  return [5, 6].includes(order);
}

function isFinalTest(order, variant) {
  if (variant === "taka41a" || variant === "taka41c") {
    return [8, 9, 10, 11].includes(order);
  }
  return [7, 8].includes(order);
}

function isTransit(order, variant) {
  if (variant === "taka41a") {
    return [9, 10, 11, 12, 13].includes(order);
  }
  if (variant === "taka41c") {
    return [12, 13].includes(order);
  }
  return [9, 10, 11, 12].includes(order);
}

function ShouldAddSlash({ index, cellSplits }) {
  return index !== cellSplits.length - 1 ? " / " : "";
}

function getClassName(order, classes, variant) {
  if (order === 0) return classes.takaWaferOrder;
  if (order === 1 || order === 2) return classes.takaBumpDie;
  if (order === 3 || order === 4) return classes.takaProb;
  if (isAssembly(order, variant)) return classes.takaAssembly;
  if (isFinalTest(order, variant)) return classes.takaFinalTest;
  if (isTransit(order, variant)) return classes.takaTransit;
  return classes.takaAdvantest;
}

function renderCells({
  cell,
  order,
  isLink,
  isLastRow,
  classes,
  isDarkTheme,
  setStageFilter,
  stageFilter,
  onSetStageFilter,
  getCellStyle,
  getClassName,
  variant,
  dataSourceEntry,
  advId,
  showTooltip,
}) {
  const stageFilterClickHandler = (dataSourceEntry) => {
    setStageFilter && setStageFilter({ dataSourceEntry });
    onSetStageFilter && onSetStageFilter();
  };
  const value = getSimplifiedAdvId(cell);
  const dataArr = dataSourceEntry;
  let cellSplits = [];

  let showAslink = false;
  if (
    (variant === "taka41a" || variant === "taka41c") &&
    order > 0 &&
    order <= 13
  ) {
    showAslink = isLink;
  }
  if (variant === "taka" && order > 0 && order <= 12) {
    showAslink = isLink;
  }

  if (showAslink) {
    cellSplits = cell
      .split("/")
      .map((c) => c.trim())
      .filter((c) => c !== "");
  }

  const renderLink = (cellSplits) => (
    <span className={classes.tootip}>
      {cellSplits.length > 0 &&
        cellSplits.map((c, index) => (
          <React.Fragment key={`${c}`}>
            <span
              data-testid={`unit-link-${c}`}
              className={classes.unitsLink}
              onClick={() => {
                if (variant === "taka41a" || variant === "taka41c") {
                  stageFilterClickHandler(dataArr[index]);
                } else {
                  stageFilterClickHandler(dataArr);
                }
              }}
            >
              <Tooltip
                title={
                  <>
                    <p>{advId.split(" OR ")[index]}</p>
                    <p>Click to filter</p>
                  </>
                }
              >
                {c}
              </Tooltip>
            </span>
            {ShouldAddSlash({ index, cellSplits })}
          </React.Fragment>
        ))}
    </span>
  );
  return (
    <TableCell
      key={order}
      variant="head"
      className={getClassName(order, classes, variant)}
      style={getCellStyle(order, isLastRow, isDarkTheme, variant)}
    >
      {showAslink ? (
        renderLink(cellSplits)
      ) : (
        <p>{addToolTipToCell(value, advId, showTooltip)}</p>
      )}
    </TableCell>
  );
}

function addToolTipToCell(cell, advId, showTooltip) {
  if (typeof cell !== "string") return cell;
  if (cell && cell.includes("/") && showTooltip) {
    const cellSplits = cell
      .split("/")
      .map((c) => c.trim())
      .filter((c) => c !== "");
    return (
      <span>
        {cellSplits.length > 0 &&
          cellSplits.map((c, index) => (
            <React.Fragment key={`${c}-${advId.split(" OR ")[index]}`}>
              <span>
                <Tooltip title={advId.split(" OR ")[index]}>{c}</Tooltip>
              </span>
              {index !== cellSplits.length - 1 && " / "}
            </React.Fragment>
          ))}
      </span>
    );
  } else if (cell && showTooltip) {
    return (
      <Tooltip title={advId}>
        <span>{cell}</span>
      </Tooltip>
    );
  }
  return cell;
}

function getFinalTestCellStyle(isDarkTheme, minWidth) {
  if (isDarkTheme) {
    return { borderRight: "2px solid #A8ABC2", minWidth };
  } else {
    return { minWidth };
  }
}

function getManufacturingStepCells({
  classes,
  isDarkTheme,
  variant,
  selectedTaka,
  getStickyStyleFirstCol,
}) {
  const cells = [
    {
      label: "Manufacturing step",
      className: getStickyStyleFirstCol(),
      style: { background: "none", maxWidth: 50 },
    },
    { label: "Wafer Orders", className: classes.takaWaferOrder },
    { label: "Bump Die Bank", className: classes.takaBumpDie },
    {
      label: "Bump",
      className: classes.takaBumpDie,
      style: getFinalTestCellStyle(isDarkTheme, 105),
    },
    { label: "Probe Die Bank", className: classes.takaProb },
    {
      label: "Probe",
      className: classes.takaProb,
      style: isDarkTheme ? { borderRight: "2px solid #A8ABC2" } : {},
    },
    { label: "Assembly Die Bank", className: classes.takaAssembly },
  ];

  // Helper function to create common cell structure for taka41 variants
  const createTaka41Cells = (firstSuffix, secondSuffix) => [
    {
      label: `Assembly (${firstSuffix})`,
      className: classes.takaAssembly,
      style: getFinalTestCellStyle(false, 130),
    },
    {
      label: `Assembly (${secondSuffix})`,
      className: classes.takaAssembly,
      style: getFinalTestCellStyle(isDarkTheme, 130),
    },
    {
      label: `Final Test Die Bank (${firstSuffix})`,
      className: classes.takaFinalTest,
    },
    {
      label: `Final Test Die Bank (${secondSuffix})`,
      className: classes.takaFinalTest,
    },
    {
      label: `Final Test (${firstSuffix})`,
      className: classes.takaFinalTest,
      style: getFinalTestCellStyle(false, 130),
    },
    {
      label: `Final Test (${secondSuffix})`,
      className: classes.takaFinalTest,
      style: getFinalTestCellStyle(isDarkTheme, 130),
    },
    {
      label: `Transit (${firstSuffix})`,
      className: classes.takaTransit,
      colSpan: 1,
    },
    {
      label: `Transit (${secondSuffix})`,
      className: classes.takaTransit,
      colSpan: 1,
      style: getFinalTestCellStyle(true, 100),
    },
    {
      label: `Stock On Hand (${firstSuffix})`,
      className: classes.takaAdvantest,
      colSpan: 1,
    },
    {
      label: `Stock On Hand (${firstSuffix}-RMN)`,
      className: classes.takaAdvantest,
      colSpan: 1,
    },
    {
      label: `Stock On Hand (${secondSuffix})`,
      className: classes.takaAdvantest,
      colSpan: 1,
    },
    {
      label: `Stock On Hand (${secondSuffix}-RMN)`,
      className: classes.takaAdvantest,
      colSpan: 1,
    },
  ];

  if (variant === "taka41a") {
    cells.push(...createTaka41Cells("NA", "SA"));
  } else if (variant === "taka41c") {
    cells.push(...createTaka41Cells("NC", "SC"));
  } else {
    cells.push(
      {
        label: "Assembly",
        className: classes.takaAssembly,
        style: getFinalTestCellStyle(isDarkTheme, 130),
      },
      { label: "Final Test Die Bank", className: classes.takaFinalTest },
      {
        label: "Final Test",
        className: classes.takaFinalTest,
        style: getFinalTestCellStyle(isDarkTheme, 230),
      },
      {
        label: `Transit (${selectedTaka})`,
        className: classes.takaTransit,
        colSpan: 2,
      },
      {
        label: `Transit (${selectedTaka} D)`,
        className: classes.takaTransit,
        colSpan: 2,
        style: { borderRight: "2px solid #A8ABC2" },
      },
      {
        label: `Stock On Hand (${selectedTaka})`,
        className: classes.takaAdvantest,
        colSpan: 2,
      },
      {
        label: `Stock On Hand (${selectedTaka} D)`,
        className: classes.takaAdvantest,
        colSpan: 2,
      }
    );
  }

  cells.push(
    { label: "Sub Total Liabilities", className: classes.takaAdvantest },
    { label: "Sub Total WIP", className: classes.takaAdvantest },
    { label: "Sub Total Buffer", className: classes.takaAdvantest },
    { label: "Total", className: classes.takaAdvantest }
  );

  return cells;
}
export default function TakaTable({
  tableList,
  setStageFilter,
  stageFilter,
  onSetStageFilter,
  selectedTaka,
  selectedTakaLoader,
  variant = "taka",
}) {
  const isDarkTheme = useTheme().palette.mode === "dark";
  const theme = useTheme();

  const classes = useStyles({ theme, isDarkTheme });

  if (!tableList || tableList.length === 0) return null;

  let amkorColSpan = 12;
  if (
    selectedTaka === constants.taka41a ||
    selectedTaka === constants.taka41c
  ) {
    amkorColSpan = 13;
  }

  if (
    selectedTaka === constants.taka41a ||
    selectedTaka === constants.taka41c
  ) {
    tableList = tranformResultsForTaka41(tableList);
  }

  const getStickyStyleFirstCol = () =>
    clsx(classes.takaFirstCol, classes.sticky);

  const manufacturingStepRow = (
    <TableRow sx={{ borderBottom: "2px solid #A8ABC2" }}>
      {getManufacturingStepCells({
        classes,
        isDarkTheme,
        variant,
        selectedTaka,
        getStickyStyleFirstCol,
      }).map((cell, i) => (
        <TableCell
          key={cell.label + (cell.colSpan || "")}
          variant="head"
          className={cell.className}
          style={cell.style}
          colSpan={cell.colSpan}
          {...(i === 0
            ? {
                style: {
                  position: "sticky",
                  left: 0,
                  background: `${theme.palette.bgColor} !important`,
                  zIndex: 1001,
                  borderRight: "2px solid #A8ABC2",
                },
              }
            : {})}
        >
          {cell.label}
        </TableCell>
      ))}
    </TableRow>
  );

  return selectedTakaLoader ? (
    <Box className={classes.tablebContainerLoader}>
      <LoadingSpinner loadingMessage={constants.loading} />
    </Box>
  ) : (
    <Box className={classes.tablebContainer}>
      <Table
        sx={{
          width: "100%",
          height: "35vh",
        }}
        aria-label="Summary Table"
        stickyHeader
      >
        <TableHead>
          <TableRow style={{ borderBottom: "2px solid #A8ABC2" }}>
            <TableCell
              className={clsx(classes.tableHeadTaka, classes.sticky)}
              variant="head"
            >
              SUPPLIER
            </TableCell>
            <TableCell className={classes.tableHeadTaka} variant="head">
              TSMC/GUC
            </TableCell>
            <TableCell
              className={classes.tableHeadTaka}
              align="center"
              colSpan={amkorColSpan}
              variant="head"
            >
              AMKOR
            </TableCell>
            <TableCell
              align="center"
              colSpan={8}
              className={classes.tableHeadTaka}
              style={{ borderRight: "none" }}
              variant="head"
            >
              ADVANTEST
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Manufacturing step */}
          {manufacturingStepRow}

          {/* Manufacturing site */}
          <TableRow
            key={"Manufacturing site"}
            sx={{ borderBottom: "2px solid #A8ABC2 !important" }}
          >
            <TableCell
              component="th"
              scope="row"
              className={getStickyStyleFirstCol()}
            >
              Manufacturing site
            </TableCell>
            {tableList?.["Manufacturing site"].map((m, i) =>
              renderCells({
                cell: m,
                order: i,
                classes,
                isDarkTheme,
                getCellStyle,
                getClassName,
                variant,
              })
            )}
          </TableRow>
          {/* Unit of measure */}
          <TableRow
            key={"Unit of measure"}
            sx={{ borderBottom: "2px solid #A8ABC2 !important" }}
          >
            <TableCell
              component="th"
              scope="row"
              className={getStickyStyleFirstCol()}
            >
              Unit of measure
            </TableCell>
            {tableList?.["Unit of measure"].map((m, i) =>
              renderCells({
                cell: m,
                order: i,
                classes,
                isDarkTheme,
                getCellStyle,
                getClassName,
                variant,
              })
            )}
          </TableRow>
          {/* ADV Item Number */}
          <TableRow
            key={"ADV Item Number"}
            sx={{ borderBottom: "2px solid #A8ABC2 !important" }}
          >
            <TableCell
              component="th"
              scope="row"
              className={getStickyStyleFirstCol()}
            >
              ADV Item Number
            </TableCell>
            {tableList?.["ADV Item Number"].map((m, i) =>
              renderCells({
                cell: m,
                order: i,
                classes,
                isDarkTheme,
                getCellStyle,
                getClassName,
                variant,
              })
            )}
          </TableRow>
          {/* Units */}
          <TableRow key={"Units"}>
            <TableCell
              component="th"
              scope="row"
              className={getStickyStyleFirstCol()}
            >
              Units
            </TableCell>
            {tableList?.["Units"]?.map((m, i) =>
              renderCells({
                isLink: i <= amkorColSpan ? true : false,
                cell: m,
                order: i,
                classes,
                isDarkTheme,
                setStageFilter,
                stageFilter,
                onSetStageFilter,
                getCellStyle,
                getClassName,
                variant,
                advId: tableList?.["ADV Item Number"]?.[i],
                dataSourceEntry: tableList?.["Data source"]?.[i],
                showTooltip: true,
              })
            )}
          </TableRow>
          {/* Value (Single unit) */}
          <TableRow
            key={"Value (Single unit)"}
            sx={{ borderBottom: "2px solid #A8ABC2 !important" }}
          >
            <TableCell
              component="th"
              scope="row"
              className={getStickyStyleFirstCol()}
            >
              Value (Single unit)
            </TableCell>
            {tableList?.["Value (Single unit)"]?.map((m, i) =>
              renderCells({
                cell: m,
                order: i,
                classes,
                isDarkTheme,
                getCellStyle,
                getClassName,
                variant,
                advId: tableList?.["ADV Item Number"]?.[i],
                showTooltip: true,
              })
            )}
          </TableRow>
          {/* Value (Total) */}
          <TableRow key={"Value (Total)"} style={{}}>
            <TableCell
              component="th"
              scope="row"
              className={getStickyStyleFirstCol()}
              style={{ borderBottom: "none" }}
            >
              Value (Total)
            </TableCell>
            {tableList?.["Value (Total)"]?.map((m, i) =>
              renderCells({
                cell: m,
                order: i,
                isLastRow: true,
                classes,
                isDarkTheme,
                getCellStyle,
                getClassName,
                variant,
                advId: tableList?.["ADV Item Number"]?.[i],
                showTooltip: true,
              })
            )}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

TakaTable.propTypes = {
  tableList: PropsTypes.object,
  setStageFilter: PropsTypes.func,
  stageFilter: PropsTypes.object,
  onSetStageFilter: PropsTypes.func,
  selectedTaka: PropsTypes.string,
  selectedTakaLoader: PropsTypes.bool,
  variant: PropsTypes.oneOf(["taka", "taka41a", "taka41c"]),
};

export {
  isAssembly,
  isFinalTest,
  isTransit,
  getClassName,
  getCellStyle,
  ShouldAddSlash,
};
